import React, { useState } from 'react';
import 'mapbox-gl/dist/mapbox-gl.css';
import Map, { Marker } from 'react-map-gl/mapbox';
import { MaterialCountBadge } from '@/components/MaterialCountBadge';
import { EMaterialStatus } from '@shelflix/schemas/material/enums';
import { Button } from '@shelflix/ui-web/components/ui/button';

import mapsSvg from '@/assets/icons-normalized/maps.svg?raw';
import { DashboardCard } from '../DashboardCard';
import Icon from '@shelflix/ui-web/components/Icon';
import { cn } from '../../../../../packages/ui-web/src/lib/utils';
import { useNavigate } from 'react-router-dom';
import { writeListState } from '@/screens/MaterialList/listState';

export interface GroupedPin {
  groupId: string;
  centerLat: number;
  centerLon: number;
  isGroup: boolean;
  groupLevel?: string;
  count: number;
  statusCounts?: Array<{ status: EMaterialStatus; count: number }>;
}

export interface GeographicDistributionItem {
  groupedPins: GroupedPin[];
  setViewState: (e: { zoom: number; latitude: number; longitude: number }) => void;
}

export function GeographicDistribution({ groupedPins, setViewState }: GeographicDistributionItem) {
  const navigate = useNavigate();

  const [selectedMarker, setSelectedMarker] = useState<{
    item: {
      isGroup: boolean;
      group?: string;
      count: number;
      materials?: Array<{ status: string; count: number }>;
      pins?: Array<{
        group: string;
        count: number;
        materials?: Array<{ status: string; count: number }>;
      }>;
    };
  } | null>(null);

  const handleNavigateToMaterials = (facilityName: string) => {
    setSelectedMarker(null);
    navigate(`/pos?${writeListState({ search: facilityName, page: 1 })}`);
  };

  return (
    <DashboardCard title="Distribuição Geográfica" icon={mapsSvg} className="w-full bg-[#191919CC]">
      <div className="relative flex h-full min-h-100 w-full">
        <Map
          mapboxAccessToken={`${import.meta.env.VITE_APP_MAPBOX_TOKEN}`}
          initialViewState={{
            zoom: 12,
            latitude: -23.5489,
            longitude: -46.6388,
          }}
          onMove={(evt) => setViewState(evt.viewState)}
          onClick={() => setSelectedMarker(null)}
          style={{ width: '100%', height: '100%' }}
          mapStyle="mapbox://styles/mapbox/dark-v11"
        >
          {groupedPins.map((item) => {
            const isGroup = item.isGroup;
            const groupLevel = item.groupLevel;

            // Define título e tamanho baseado no nível de agrupamento
            let title: string;
            let pinSize: number;

            if (!isGroup) {
              title = `${item.count} dispositivos`;
              pinSize = 32;
            } else {
              switch (groupLevel) {
                case 'local':
                  title = `${item.count} dispositivos próximos`;
                  pinSize = 36;
                  break;
                case 'neighborhood':
                  title = `${item.count} dispositivos no bairro`;
                  pinSize = 40;
                  break;
                case 'city':
                  title = `${item.count} dispositivos na cidade`;
                  pinSize = 44;
                  break;
                case 'region':
                  title = `${item.count} dispositivos na região`;
                  pinSize = 48;
                  break;
                default:
                  title = `${item.count} dispositivos agrupados`;
                  pinSize = 40;
              }
            }

            // Use pre-calculated pin color and status counts from useMemo
            const pinColor = 'text-primary';
            const statusCounts = item.statusCounts || [];

            return (
              <Marker
                key={item.groupId}
                longitude={item.centerLon}
                latitude={item.centerLat}
                anchor="bottom"
                onClick={(e) => {
                  e.originalEvent.stopPropagation();
                  setSelectedMarker({
                    item,
                  });
                }}
              >
                <div title={title}>
                  {/* Material count badges */}
                  <MaterialCountBadge
                    statusCounts={statusCounts}
                    isEmpty={statusCounts.length === 0 && item.count === 0}
                  />

                  <Icon
                    name="push_pin"
                    size={pinSize}
                    className={cn(
                      'z-2',
                      pinColor,
                      isGroup && 'drop-shadow-lg',
                      groupLevel === 'region' && 'drop-shadow-xl'
                    )}
                    fill={1}
                  />
                </div>
              </Marker>
            );
          })}
        </Map>
        {/* Popup do marker */}
        {selectedMarker && (
          <div
            className="bg-opacity-50 absolute inset-0 z-50 flex items-center justify-center bg-black/50"
            onClick={() => setSelectedMarker(null)}
          >
            <div
              className="max-w-sm rounded-lg border border-gray-200 bg-white p-4 shadow-lg"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="mb-2 flex items-center justify-between">
                <h3 className="font-semibold text-gray-800">
                  {selectedMarker.item.isGroup ? 'Estabelecimentos Agrupados' : 'Estabelecimento'}
                </h3>
                <button onClick={() => setSelectedMarker(null)} className="text-gray-400 hover:text-gray-600">
                  <Icon name="close" size={16} />
                </button>
              </div>

              {selectedMarker.item.isGroup ? (
                <div className="space-y-2">
                  <p className="mb-2 text-sm text-gray-600">{selectedMarker.item.count} dispositivos agrupados</p>
                  <div className="max-h-48 space-y-1 overflow-y-auto">
                    {Object.values(
                      selectedMarker.item.pins?.reduce(
                        (acc, pin) => {
                          const facilityName = pin.group;
                          if (!acc[facilityName]) {
                            acc[facilityName] = { name: facilityName, count: 0, materials: [] };
                          }
                          acc[facilityName].count += pin.count;
                          // Aggregate materials for this facility
                          if (pin.materials) {
                            acc[facilityName].materials.push(...pin.materials);
                          }
                          return acc;
                        },
                        {} as Record<
                          string,
                          { name: string; count: number; materials: Array<{ status: string; count: number }> }
                        >
                      ) || {}
                    ).map((facility) => {
                      // Calculate status counts for this facility
                      const facilityStatusCounts = facility.materials.reduce(
                        (acc, material) => {
                          const existing = acc.find((s) => s.status === material.status);
                          if (existing) {
                            existing.count += material.count;
                          } else {
                            acc.push({ status: material.status as EMaterialStatus, count: material.count });
                          }
                          return acc;
                        },
                        [] as Array<{ status: EMaterialStatus; count: number }>
                      );

                      return (
                        <div
                          key={facility.name}
                          className="flex cursor-pointer items-center justify-between rounded p-2 hover:bg-gray-50"
                          onClick={() => handleNavigateToMaterials(facility.name)}
                        >
                          <div className="flex flex-col">
                            <span className="text-sm font-medium text-gray-700">{facility.name}</span>
                            <span className="text-xs text-gray-500">{facility.count} dispositivos</span>
                          </div>
                          <div className="relative flex flex-row gap-1">
                            <MaterialCountBadge
                              statusCounts={facilityStatusCounts}
                              isEmpty={facility.count === 0}
                              className="static"
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ) : (
                <div className="space-y-2">
                  <p className="text-sm font-medium text-gray-700">{selectedMarker.item.group || 'Estabelecimento'}</p>
                  <div className="flex items-center gap-2">
                    <p className="text-xs text-gray-500">{selectedMarker.item.count} dispositivos</p>
                    <div className="relative flex flex-row gap-1">
                      <MaterialCountBadge
                        statusCounts={
                          selectedMarker.item.materials?.reduce(
                            (acc, material) => {
                              const existing = acc.find((s) => s.status === material.status);
                              if (existing) {
                                existing.count += material.count;
                              } else {
                                acc.push({ status: material.status as EMaterialStatus, count: material.count });
                              }
                              return acc;
                            },
                            [] as Array<{ status: EMaterialStatus; count: number }>
                          ) || []
                        }
                        isEmpty={selectedMarker.item.count === 0}
                        className="static"
                      />
                    </div>
                  </div>
                  <Button
                    variant="default"
                    onClick={() => selectedMarker.item.group && handleNavigateToMaterials(selectedMarker.item.group)}
                    className="mt-2 w-full"
                  >
                    Ver Materiais
                  </Button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
      <div className="flex w-full gap-6 rounded-[5px] bg-[#0E0E0E] p-3">
        <div className="flex items-center gap-1.5">
          <span className="h-3.75 w-3.75 rounded-full bg-[#28A745]" />
          <span className="text-xs font-normal text-[#B2B2B2]">Online</span>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="h-3.75 w-3.75 rounded-full bg-[#DC3545]" />
          <span className="text-xs font-normal text-[#B2B2B2]">Offline</span>
        </div>
      </div>
    </DashboardCard>
  );
}
